import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "movesoffer_cookie_consent";
const COOKIE_CONSENT_EXPIRES_DAYS = 365;

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
  timestamp: number;
}

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true
    analytics: false,
    advertising: false,
    timestamp: Date.now(),
  });

  const enableAnalytics = useCallback(() => {
    // Enable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
  }, []);

  const disableAnalytics = useCallback(() => {
    // Disable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
  }, []);

  const enableAdvertising = useCallback(() => {
    // Enable advertising cookies
    if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted'
      });
    }
  }, []);

  const disableAdvertising = useCallback(() => {
    // Disable advertising cookies
    if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      });
    }
  }, []);

  const applyConsent = useCallback((prefs: CookiePreferences) => {
    // Apply analytics consent
    if (prefs.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }

    // Apply advertising consent
    if (prefs.advertising) {
      enableAdvertising();
    } else {
      disableAdvertising();
    }
  }, [enableAnalytics, disableAnalytics, enableAdvertising, disableAdvertising]);

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        const expiryTime = parsed.timestamp + (COOKIE_CONSENT_EXPIRES_DAYS * 24 * 60 * 60 * 1000);
        
        // If consent is expired, show banner again
        if (Date.now() > expiryTime) {
          localStorage.removeItem(COOKIE_CONSENT_KEY);
          setShowBanner(true);
        } else {
          applyConsent(parsed);
        }
      } catch (e) {
        setShowBanner(true);
      }
    } else {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, [applyConsent]);

  const saveConsent = (prefs: CookiePreferences) => {
    const consentData = {
      ...prefs,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    applyConsent(consentData);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      advertising: true,
      timestamp: Date.now(),
    };
    saveConsent(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      advertising: false,
      timestamp: Date.now(),
    };
    saveConsent(essentialOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const handleToggleSettings = () => {
    setShowSettings(!showSettings);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl z-50 border-t-4 border-primary">
        <div className="container mx-auto p-6 max-w-6xl">
          {!showSettings ? (
            // Simple Banner View
            <div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg
                    className="w-8 h-8 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    We use cookies to enhance your experience, analyze site
                    traffic, and deliver personalized content. By clicking
                    "Accept All," you consent to our use of cookies.{" "}
                    <Link
                      to="/cookies"
                      className="text-primary underline hover:text-primary-dark"
                    >
                      Cookie Policy
                    </Link>{" "}
                    |{" "}
                    <Link
                      to="/privacy"
                      className="text-primary underline hover:text-primary-dark"
                    >
                      Privacy Policy
                    </Link>
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors font-medium"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={handleRejectNonEssential}
                      className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium"
                    >
                      Reject Non-Essential
                    </button>
                    <button
                      onClick={handleToggleSettings}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                    >
                      Customize Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Detailed Settings View
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Cookie Preferences
                </h3>
                <button
                  onClick={handleToggleSettings}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close settings"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Customize which cookies you want to allow. Note that essential
                cookies cannot be disabled as they are required for the site to
                function.
              </p>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {/* Essential Cookies */}
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        Essential Cookies (Required)
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        These cookies are necessary for the website to function
                        and cannot be disabled. They enable core functionality
                        such as security, authentication, and accessibility.
                      </p>
                    </div>
                    <div className="ml-4">
                      <div className="w-12 h-6 bg-primary rounded-full opacity-50 cursor-not-allowed relative">
                        <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Always On</p>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        Analytics Cookies
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        These cookies help us understand how visitors interact
                        with our website by collecting anonymous usage data. This
                        helps us improve our services.
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() =>
                          setPreferences({
                            ...preferences,
                            analytics: !preferences.analytics,
                          })
                        }
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          preferences.analytics ? "bg-primary" : "bg-gray-300"
                        }`}
                        aria-label="Toggle analytics cookies"
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.analytics
                              ? "right-1"
                              : "left-1"
                          }`}
                        ></div>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Advertising Cookies */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        Advertising Cookies
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        These cookies are used to deliver relevant advertisements
                        and marketing campaigns. They track your browsing to show
                        you ads that match your interests.
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() =>
                          setPreferences({
                            ...preferences,
                            advertising: !preferences.advertising,
                          })
                        }
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          preferences.advertising ? "bg-primary" : "bg-gray-300"
                        }`}
                        aria-label="Toggle advertising cookies"
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.advertising
                              ? "right-1"
                              : "left-1"
                          }`}
                        ></div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors font-medium"
                >
                  Save My Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium"
                >
                  Accept All
                </button>
                <button
                  onClick={handleRejectNonEssential}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                >
                  Reject Non-Essential
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Learn more:{" "}
                <Link
                  to="/cookies"
                  className="text-primary underline hover:text-primary-dark"
                >
                  Cookie Policy
                </Link>{" "}
                |{" "}
                <Link
                  to="/privacy"
                  className="text-primary underline hover:text-primary-dark"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
